import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  Grid,
  IconButton,
  Snackbar,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ClientDetailsModal from "../components/ClientDetailsModal";
import LoadingIndicator from "../components/LoadingIndicator";


const ClientManagementPage = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [search, setSearch] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState({ message: "", type: "" });
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // States for subscription management.
  const [subscriptionPlan, setSubscriptionPlan] = useState("");
  const [subscriptionNote, setSubscriptionNote] = useState("");
  const [extendSubscription, setExtendSubscription] = useState(false);
  // States for PIN & Lock management.
  const [newPin, setNewPin] = useState("");
  const [isLocked, setIsLocked] = useState(false);

  const [activeTab, setActiveTab] = useState("1");

const handleTabChange = (event, newValue) => {
  setActiveTab(newValue);
};

  // Fetch clients on mount.
  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get("http://localhost:5000/api/clients", { withCredentials: true });
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  // Filter clients by search term.
  const filteredClients = useMemo(() => {
    const term = search.toLowerCase();
    return clients.filter((client) =>
      client.mac.toLowerCase().includes(term) ||
      (client.resellerNote && client.resellerNote.toLowerCase().includes(term))
    );
  }, [clients, search]);

  const columns = [
    { field: "mac", headerName: "MAC Address", flex: 1 },
    {
      field: "country",
      headerName: "Country",
      flex: 1,
      renderCell: (params) =>
        params.row.info && params.row.info.country ? params.row.info.country : "N/A",
    },
    {
      field: "subscriptionEnd",
      headerName: "Subscription End",
      flex: 1,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : "N/A",
    },
    { field: "paymentPlan", headerName: "Payment Plan", flex: 1 },
    { field: "resellerNote", headerName: "Note", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setSelectedClient(params.row);
              setIsLocked(params.row.locked || false);
              // Reset subscription management state when opening details.
              setSubscriptionPlan("");
              setSubscriptionNote("");
              setExtendSubscription(false);
              setOpenDetailsModal(true);
            }}
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              mr: 1,
              "&:hover": { backgroundColor: "primary.dark" },
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setSelectedClient(params.row);
              setOpenEditModal(true);
            }}
            sx={{
              backgroundColor: "secondary.main",
              color: "white",
              mr: 1,
              "&:hover": { backgroundColor: "secondary.dark" },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClient(params.row._id);
            }}
            sx={{
              backgroundColor: "secondary.main",
              color: "white",
              "&:hover": { backgroundColor: "secondary.dark" },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleDeleteClient = async (id) => {
    if (window.confirm("Are you sure you want to delete this client?")) {
      try {
        await axios.delete(`http://localhost:5000/api/clients/${id}`, { withCredentials: true });
        setSnackbarMessage({ message: "Client deleted successfully", type: "success" });
        setOpenSnackbar(true);
        fetchClients();
      } catch (error) {
        console.error("Error deleting client:", error);
      }
    }
  };

  // Function to activate/extend a subscription.
  const handleActivateSubscription = async () => {
    if (!subscriptionPlan) {
      setSnackbarMessage({ message: "Please select a subscription plan.", type: "warning" });
      setOpenSnackbar(true);
      return;
    }
    try {
      const response = await axios.put(
        "http://localhost:5000/api/clients/subscription/" + selectedClient.mac,
        {
          paymentPlan: subscriptionPlan,
          expand: extendSubscription,
          subscriptionStatus: "active",
        },
        { withCredentials: true }
      );
      setSnackbarMessage({ message: "Subscription updated successfully", type: "success" });
      setOpenSnackbar(true);
      fetchClients();
      // Optionally update selectedClient with new subscription data.
      // setSelectedClient({
      //   ...selectedClient,
      //   subscriptionEnd: response.data.order.subscriptionEnd,
      //   paymentPlan: subscriptionPlan,
      // });
    } catch (error) {
      console.error("Error updating subscription:", error);
      setSnackbarMessage({ message: "Error updating subscription", type: "error" });
      setOpenSnackbar(true);
    }
  };

  // Function to update client PIN.
  const updateClientPin = async () => {
    if (!newPin) {
      setSnackbarMessage({ message: "Please enter a new PIN.", type: "warning" });
      setOpenSnackbar(true);
      return;
    }
    try {
      await axios.put(
        `http://localhost:5000/api/clients/${selectedClient._id}/pin`,
        { pin: newPin },
        { withCredentials: true }
      );
      setSnackbarMessage({ message: "Client PIN updated successfully", type: "success" });
      setOpenSnackbar(true);
      fetchClients();
      setSelectedClient({ ...selectedClient, pin: newPin });
    } catch (error) {
      console.error("Error updating client PIN:", error);
      setSnackbarMessage({ message: "Error updating client PIN", type: "error" });
      setOpenSnackbar(true);
    }
  };

  // Function to toggle the client's lock state.
  const toggleClientLock = async () => {
    try {
      await axios.put(
        `http://localhost:5000/api/clients/${selectedClient._id}/lock`,
        {},
        { withCredentials: true }
      );
      setSnackbarMessage({ message: "Client lock status updated", type: "success" });
      setOpenSnackbar(true);
      fetchClients();
      setIsLocked(!isLocked);
    } catch (error) {
      console.error("Error toggling client lock:", error);
      setSnackbarMessage({ message: "Error toggling client lock", type: "error" });
      setOpenSnackbar(true);
    }
  };

  if (!clients || !clients.length) {
    return <LoadingIndicator message="Fetching Clients data..." />;
}

  return (
    <Box sx={{ padding: 4 }}>
      <Typography variant="h4" gutterBottom>
        Client Management
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button variant="contained" onClick={() => {/* Open add client modal if needed */}}>
          Add Client
        </Button>
      </Box>

      <DataGrid
        rows={filteredClients}
        columns={columns}
        getRowId={(row) => row._id}
        autoHeight
        pageSize={5}
        disableSelectionOnClick
        sx={{ backgroundColor: "white" }}
        onRowClick={(params) => {
          // Open details modal when a row is clicked.
          setSelectedClient(params.row);
          setIsLocked(params.row.locked || false);
          setSubscriptionPlan("");
          setSubscriptionNote("");
          setExtendSubscription(false);
          setOpenDetailsModal(true);
        }}
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarMessage.type}>
          {snackbarMessage.message}
        </Alert>
      </Snackbar>

      {/* Client Details Modal with Subscription & Playlist Management */}
      <ClientDetailsModal
        openDetailsModal={openDetailsModal}
        setOpenDetailsModal={setOpenDetailsModal}
        selectedClient={selectedClient}
        subscriptionPlan={subscriptionPlan}
        setSubscriptionPlan={setSubscriptionPlan}
        subscriptionNote={subscriptionNote}
        setSubscriptionNote={setSubscriptionNote}
        extendSubscription={extendSubscription}
        setExtendSubscription={setExtendSubscription}
        newPin={newPin}
        setNewPin={setNewPin}
        isLocked={isLocked}
        toggleClientLock={() => {}}
        handleActivateSubscription={() => {handleActivateSubscription()}}
        updateClientPin={() => {}}
      />

      {/* Edit Client Modal */}
      <Modal
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            width: 400,
            bgcolor: "background.paper",
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          {selectedClient ? (
            <>
              <Typography variant="h6" gutterBottom>
                Edit Client
              </Typography>
              <TextField
                label="MAC Address"
                fullWidth
                margin="normal"
                value={selectedClient.mac}
                onChange={(e) => setSelectedClient({ ...selectedClient, mac: e.target.value })}
              />
              <TextField
                label="Note"
                fullWidth
                margin="normal"
                value={selectedClient.resellerNote || ""}
                onChange={(e) => setSelectedClient({ ...selectedClient, resellerNote: e.target.value })}
              />
              <Button variant="contained" sx={{ mt: 2 }} onClick={() => setOpenEditModal(false)}>
                Save
              </Button>
            </>
          ) : (
            <Typography>Loading details...</Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default ClientManagementPage;
